import { useState } from "react";
import PropTypes from "prop-types";
import { startCase } from "lodash";
import capitalize from "capitalize";
import Modal from "@shared/v2/Modal";
import { UserSolidV6 } from "@shared/v2/Icomoon";
import { stripHtml } from "../../../shared/Utilities";
import IntentButton from "../../../PersonDetail/components/IntentDropdown/IntentButton";
import BaseEvent from "./BaseEvent";
import { isServiceUpdater } from "./helpers/updaterAttribution";

const SHORT_STATUS = {
  new: "New",
  unqualified: "Unqual",
  watch: "Watch",
  nurture: "Nurt",
  hot: "Hot",
  "past-client": "Past",
  inactive: "Inactiv",
  archived: "Arch",
  trash: "Trash",
  pending: "Pending",
  "prospective-client": "Prospective",
  "active-client": "Active",
  buyer: "Buyer",
  seller: "Seller",
  "seller/buyer": "Seller & Buyer",
  tenant: "Tenant",
  landlord: "Landlord",
  recruit: "Recruit",
  candidate: "Candidate",
  "n/a": "N/A",
};

const statusDetail = (event, change) => {
  const prevStatus = change[0];
  const newStatus = change[1];

  const prevClasses = `btn btn-sm person-status-${prevStatus}`;
  const newClasses = `btn btn-sm person-status-${newStatus}`;

  const prevShortStatus = SHORT_STATUS[prevStatus];
  const newShortStatus = SHORT_STATUS[newStatus];

  return (
    <div key={`${event.id}-status-${change}`}>
      <div>
        {"Status: "}
        {prevStatus && (
          <span>
            <span className={prevClasses}>{prevShortStatus}</span>
            {" to "}
          </span>
        )}
        <span className={newClasses}>{newShortStatus}</span>
      </div>
    </div>
  );
};

const stageDetail = (event, change) => {
  const prevStage = change[0];
  const newStage = change[1];
  return (
    <div key={`${event.id}-stage-${change}`}>
      {"Stage: "}
      {prevStage && (
        <span>
          <span>{prevStage}</span>
          {" to "}
        </span>
      )}
      <span>{newStage}</span>
    </div>
  );
};

const personTypeDetail = (event, change) => {
  const prevPersonType = change[0];
  const newPersonType = change[1];
  return (
    <div key={`${event.id}-person-type-${change}`}>
      {"Person Type: "}
      {prevPersonType && (
        <span>
          <span>{capitalize(prevPersonType)}</span>
          {" to "}
        </span>
      )}
      <span>{capitalize(newPersonType)}</span>
    </div>
  );
};

const intentDetail = (event, change) => {
  const prevStatus = change[0];
  const newStatus = change[1];
  const prevShortStatus = SHORT_STATUS[prevStatus];
  const newShortStatus = SHORT_STATUS[newStatus];

  return (
    <div key={`${event.id}-status-${change}`}>
      {"Intent: "}
      {prevStatus && (
        <span>
          <IntentButton type={prevStatus} label={prevShortStatus} />
          {" to "}
        </span>
      )}
      <IntentButton type={newStatus} label={newShortStatus} />
    </div>
  );
};

const standardDetail = (event, titleizedAttribute, change) => {
  const zeroDate = ["0000-", ""];
  return (
    <div key={`${event.id}-${titleizedAttribute}-${change}`}>
      {`${titleizedAttribute}: `}

      {change[0] && (
        <span className="timeline-person-original-field">
          {"(was "}
          <span>{stripHtml(change[0].replace(...zeroDate))}</span>
          {") "}
        </span>
      )}

      {!change[1] ? (
        <span className="timeline-person-removed-field">removed</span>
      ) : (
        <span>{stripHtml(change[1].replace(...zeroDate))}</span>
      )}
    </div>
  );
};

const TimelineLeadUpdateEvent = ({ event, event: { timeline_fields: timelineFields } }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const details = Object.keys(timelineFields)
    .sort()
    .map((attribute) => {
      if (attribute === "updater_name" || attribute === "updater_type") return null;
      const titleizedAttribute =
        attribute === "anniversary" ? `Home ${startCase(attribute)}` : startCase(attribute);
      const change = timelineFields[attribute];

      if (titleizedAttribute === "Status") return statusDetail(event, change);
      if (titleizedAttribute === "Lead Type") return intentDetail(event, change);
      if (titleizedAttribute === "Stage") return stageDetail(event, change);
      if (titleizedAttribute === "Person Type") return personTypeDetail(event, change);
      return standardDetail(event, titleizedAttribute, change);
    });

  return (
    <BaseEvent
      icon={<UserSolidV6 />}
      title={
        isServiceUpdater(timelineFields) ? (
          <div>
            Automatically updated by{" "}
            <span className="tw-font-semibold">{timelineFields.updater_name}</span>
          </div>
        ) : (
          <div>
            <span className="tw-font-semibold">{timelineFields.updater_name} </span> updated profile
          </div>
        )
      }
      interactedAt={event.interacted_at_absolute}
      onClick={() => setIsModalOpen(true)}
    >
      {details}
      <Modal
        dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
        contentClassName="tw-max-w-[480px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px]"
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
      >
        <Modal.Header title="Profile Updated" closeButton />
        <Modal.Body>
          <div className="tw-font-semibold">Details:</div>
          {details}
        </Modal.Body>
      </Modal>
    </BaseEvent>
  );
};

TimelineLeadUpdateEvent.propTypes = {
  event: PropTypes.shape().isRequired,
};

export default TimelineLeadUpdateEvent;
